import Api, { handleOPRestApiError } from '@op/opds.api-base/dist';
import {
  OFFICE_SEARCH_BASE_URL,
  AUTOSUGGEST_URL,
  SEARCH_URL,
  OFFICE_DETAILS_URL,
  GET_CSRF_TOKEN_URL
} from '../utils/constants';
//import { ApiErrorResponse } from '../utils/types';

const officeSearchApi = new Api(
  `${window.BASE_URL}/${OFFICE_SEARCH_BASE_URL}`,
  { onError: handleOPRestApiError }
);

const csrfTokenApi = new Api(
  `${window.BASE_URL}`,
  { onError: handleOPRestApiError }
);

const requestHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

// /**
//  * Retrieves a CSRF token from the server.
//  * @returns A promise resolving to the CSRF token string.
//  */
const getCsrfToken = async () => {
  try {
    const response = await csrfTokenApi.get(`${GET_CSRF_TOKEN_URL}`);
    const csrfToken = response?.token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

/**
 * Provides API methods for fetching office-related data such as autosuggestions, office lists, and office details from java servlet.
 */

export const fetchOfficeApi = {
  /**
   * Fetches office autosuggestions based on the provided query string.
   * @param query - The search query string for autosuggestions.
   * @returns A promise resolving to the autosuggest results.
   * @throws Error if the query is not provided.
   */
  fetchAutoSuggest: async (query: string, currentLocale: string) => {
    if (!query) {
      throw new Error('Request or request.query is undefined');
    }
    const csrfToken = await getCsrfToken();
    return officeSearchApi.get(`${AUTOSUGGEST_URL}`,
      {
        headers: {
          ...requestHeaders,
          'Accept-language': currentLocale,
          ...(csrfToken ? { 'X_CSRF_TOKEN': csrfToken } : {})
        },
        query: {
          searchTerm: query
        }
      }
    );
  },

  /**
   * Fetches a list of offices matching the provided search term.
   * @param searchTerm - The search term to filter offices.
   * @returns A promise resolving to the list of offices.
   * @throws Error if the searchTerm is not provided.
   */
  fetchOfficeList: async (searchTerm: string, currentLocale: string) => {
    if (!searchTerm) {
      throw new Error('Request or request.searchTerm is undefined');
    }
    const csrfToken = await getCsrfToken();
    return officeSearchApi.get(`${SEARCH_URL}`,
      {
        headers: {
          ...requestHeaders,
          'Accept-language': currentLocale,
          ...(csrfToken ? { 'X_CSRF_TOKEN': csrfToken } : {})
        },
        query: {
          searchTerm
        }
      }
    );
  },

  /**
   * Fetches details for a specific office branch and company.
   * @param branchId - The ID of the office branch.
   * @param companyId - The ID of the company.
   * @returns A promise resolving to the office details.
   * @throws Error if either branchId or companyId is not provided.
   */
  fetchOfficeDetails: async (
    branchId: string,
    companyId: string,
    currentLocale: string
  ) => {
    if (!branchId || !companyId) {
      throw new Error('Branch ID and Company ID are required');
    }
    const csrfToken = await getCsrfToken();
    return officeSearchApi.get(`${OFFICE_DETAILS_URL}`, {
      headers: {
        ...requestHeaders,
        'Accept-language': currentLocale,
        ...(csrfToken ? { 'X_CSRF_TOKEN': csrfToken } : {})
      },
      query: {
        branchId: branchId,
        companyId: companyId
      }
    });
  }
};
